import * as Dialog from '@radix-ui/react-dialog'
import { Plus, PushPin, X, XSquare } from '@phosphor-icons/react'
import { useState, type CSSProperties } from 'react'
import { cn } from '@/design-system'
import { useT } from '@/i18n'
import { useTabs, type WorkspaceTab } from '@/stores/tabs'
import { useTabMeta } from './tabMeta'
import { shownUrl } from './TabStrip'

/**
 * Phones: no strip (no room). A tabs button with the count inside it (like a phone browser) opens a bottom sheet
 * of cards, each a mini preview of its page: coloured header with the page icon, title, path.
 */
export function MobileTabsButton({ onNewTab }: { onNewTab: () => void }) {
  const { locale } = useT()
  const ar = locale === 'ar'
  const count = useTabs((s) => s.tabs.length)
  const activeUrl = useTabs((s) => s.tabs.find((t) => t.id === s.activeId)?.url ?? '')
  const activeTitle = useTabMeta(activeUrl).title
  const [open, setOpen] = useState(false)
  return (
    <>
      <button type="button" className="ws-mobile-btn inline-flex max-w-[45vw] items-center gap-1.5 md:hidden" onClick={() => setOpen(true)} aria-label={ar ? `علامات التبويب (${count})` : `Tabs (${count})`}>
        <span key={count} className="ws-mobile-count tabular">{count}</span>
        {/* the tab you are on, so the button is not a bare number */}
        {activeTitle && <span className="hidden min-w-0 truncate text-xs font-semibold text-fg sm:inline">{activeTitle}</span>}
      </button>
      <MobileTabsSheet open={open} onOpenChange={setOpen} onNewTab={onNewTab} />
    </>
  )
}

export function MobileTabsSheet({ open, onOpenChange, onNewTab }: { open: boolean; onOpenChange: (o: boolean) => void; onNewTab: () => void }) {
  const { locale } = useT()
  const ar = locale === 'ar'
  const tabs = useTabs((s) => s.tabs)
  const activeId = useTabs((s) => s.activeId)
  const others = tabs.filter((t) => t.id !== activeId && !t.pinned).length
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/25 backdrop-blur-[1.5px] data-[state=open]:animate-fade-in md:hidden" />
        <Dialog.Content className="ws-sheet md:hidden" aria-describedby={undefined}>
          <div className="mx-auto mb-2 mt-2.5 h-1.5 w-10 rounded-full bg-border-strong" aria-hidden />
          <div className="flex items-center gap-2 px-4 pb-3">
            <Dialog.Title className="flex-1 text-base font-bold text-fg-strong">
              {ar ? 'علامات التبويب' : 'Tabs'} <span className="tabular text-sm font-semibold text-fg-muted">({tabs.length})</span>
            </Dialog.Title>
            {others > 0 && (
              <button type="button" className="flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-xs font-semibold text-fg-muted hover:bg-surface-hover" onClick={() => useTabs.getState().closeOthers(activeId)}>
                <XSquare size={15} aria-hidden />
                {ar ? 'إغلاق الباقي' : 'Close others'}
              </button>
            )}
            <Dialog.Close className="flex size-8 items-center justify-center rounded-lg text-fg-muted hover:bg-surface-hover" aria-label={ar ? 'إغلاق' : 'Close'}>
              <X size={17} aria-hidden />
            </Dialog.Close>
          </div>
          <ul className="grid min-h-0 flex-1 grid-cols-2 content-start gap-3 overflow-y-auto overscroll-contain px-4 pb-4">
            {tabs.map((t, i) => (
              <TabCard key={t.id} tab={t} index={i} active={t.id === activeId} onPick={() => onOpenChange(false)} />
            ))}
          </ul>
          <div className="border-t border-border px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3">
            <button
              type="button"
              className="flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-fg text-sm font-semibold text-fg-inverse active:scale-[.98]"
              onClick={() => {
                onOpenChange(false)
                onNewTab()
              }}
            >
              <Plus size={17} weight="bold" aria-hidden />
              {ar ? 'فتح صفحة في علامة تبويب جديدة' : 'Open a page in a new tab'}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

function TabCard({ tab, index, active, onPick }: { tab: WorkspaceTab; index: number; active: boolean; onPick: () => void }) {
  const { locale } = useT()
  const ar = locale === 'ar'
  const meta = useTabMeta(tab.url)
  const I = meta.icon
  return (
    <li className="ws-card" data-active={active} style={{ '--hue': meta.hue, animationDelay: `${Math.min(index, 8) * 30}ms` } as CSSProperties}>
      <button
        type="button"
        className="block w-full text-start"
        aria-current={active ? 'page' : undefined}
        onClick={() => {
          useTabs.getState().focus(tab.id)
          onPick()
        }}
      >
        {/* the mini preview: a tinted page header with the icon and a few ghost lines of content */}
        <span className="ws-card-preview" aria-hidden>
          <span className="ws-card-icon">
            <I size={22} weight="duotone" />
          </span>
          <span className="ws-card-lines">
            <span style={{ width: '70%' }} />
            <span style={{ width: '92%' }} />
            <span style={{ width: '54%' }} />
          </span>
        </span>
        <span className="block px-2.5 pb-2.5 pt-2">
          <span className="flex items-center gap-1 text-[13px] font-bold leading-tight text-fg-strong">
            {tab.pinned && <PushPin size={11} weight="fill" className="shrink-0 text-fg-subtle" aria-label={ar ? 'مثبت' : 'Pinned'} />}
            <span className="line-clamp-2">{meta.title}</span>
          </span>
          {meta.hint && <span className="mt-0.5 block truncate text-2xs font-semibold text-fg-muted" dir="auto">{meta.hint}</span>}
          <span className="mt-1 block truncate font-mono text-[10px] text-fg-subtle" dir="ltr">{shownUrl(tab.url)}</span>
        </span>
      </button>
      {!tab.pinned && (
        <button type="button" className={cn('ws-card-close')} aria-label={ar ? `إغلاق ${meta.title}` : `Close ${meta.title}`} onClick={() => useTabs.getState().close(tab.id)}>
          <X size={12} weight="bold" aria-hidden />
        </button>
      )}
    </li>
  )
}
